// src/components/ListMetricsBar.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEYS = {
  SELECTED_METRICS: 'list_selected_metrics',
  TAX_RATE: 'list_tax_rate',
  BUDGET: 'list_budget'
};

// Valores por defecto
const DEFAULT_TAX_RATE = 9.5;
const DEFAULT_BUDGET = 100.00;

const METRIC_IDS = [
  'inList',
  'inListPlusTax',
  'checked',
  'checkedPlusTax',
  'unchecked',
  'uncheckedPlusTax',
  'checkedVsBudget',
  'uncheckedVsBudget',
  'inListVsBudget',
];

const METRIC_TYPES = {
  inList: 'currency',
  inListPlusTax: 'currency',
  checked: 'currency',
  checkedPlusTax: 'currency',
  unchecked: 'currency',
  uncheckedPlusTax: 'currency',
  checkedVsBudget: 'currencyNegative',
  uncheckedVsBudget: 'currencyNegative',
  inListVsBudget: 'currencyNegative',
};

// Backward compat export
const METRICS = METRIC_IDS.map(id => ({ id, type: METRIC_TYPES[id] }));

// Selección por defecto (In list, Checked + taxes, Checked vs Budget)
const DEFAULT_SELECTED_METRICS = ['inList', 'checkedPlusTax', 'checkedVsBudget'];

const ListMetricsBar = ({ items, onOpenModal }) => {
  const { t } = useTranslation();
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [taxRate, setTaxRate] = useState(DEFAULT_TAX_RATE);
  const [budget, setBudget] = useState(DEFAULT_BUDGET);

  // Cargar desde localStorage al montar
  useEffect(() => {
    const savedMetrics = localStorage.getItem(STORAGE_KEYS.SELECTED_METRICS);
    if (savedMetrics) {
      try {
        const parsed = JSON.parse(savedMetrics);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSelectedMetrics(parsed);
        } else {
          setSelectedMetrics(DEFAULT_SELECTED_METRICS);
        }
      } catch (e) {
        setSelectedMetrics(DEFAULT_SELECTED_METRICS);
      }
    } else {
      setSelectedMetrics(DEFAULT_SELECTED_METRICS);
    }

    const savedTaxRate = localStorage.getItem(STORAGE_KEYS.TAX_RATE);
    if (savedTaxRate) {
      const parsed = parseFloat(savedTaxRate);
      if (!isNaN(parsed) && parsed >= 0) {
        setTaxRate(parsed);
      }
    }

    const savedBudget = localStorage.getItem(STORAGE_KEYS.BUDGET);
    if (savedBudget) {
      const parsed = parseFloat(savedBudget);
      if (!isNaN(parsed) && parsed >= 0) {
        setBudget(parsed);
      }
    }
  }, []);

  // Calcular valores de las métricas
  const calculateMetrics = () => {
    // Calcular totales base
    let inListTotal = 0;
    let checkedTotal = 0;
    let uncheckedTotal = 0;

    items.forEach(item => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      const total = price * quantity;
      const status = parseInt(item.status);

      inListTotal += total;
      
      if (status === 1) {
        checkedTotal += total;
      } else {
        uncheckedTotal += total;
      }
    });

    const taxMultiplier = 1 + (taxRate / 100);
    
    const inListPlusTax = inListTotal * taxMultiplier;
    const checkedPlusTax = checkedTotal * taxMultiplier;
    const uncheckedPlusTax = uncheckedTotal * taxMultiplier;
    
    const inListVsBudget = budget - inListPlusTax;
    const checkedVsBudget = budget - checkedPlusTax;
    const uncheckedVsBudget = budget - uncheckedPlusTax;

    return {
      inList: inListTotal,
      inListPlusTax,
      checked: checkedTotal,
      checkedPlusTax,
      unchecked: uncheckedTotal,
      uncheckedPlusTax,
      checkedVsBudget,
      uncheckedVsBudget,
      inListVsBudget
    };
  };

  const metrics = calculateMetrics();

  // Formatear moneda
  const formatCurrency = (value) => {
    return `$${Math.abs(value).toFixed(2)}`;
  };

  // Renderizar una métrica individual
  const renderMetric = (metricId) => {
    const metricType = METRIC_TYPES[metricId];
    if (!metricType) return null;

    const value = metrics[metricId];
    const isNegative = value < 0;
    const isNegativeType = metricType === 'currencyNegative';

    const textColorClass = (isNegativeType && isNegative) ? 'text-red-600' : 'text-gray-900';

    return (
      <div key={metricId} className="flex flex-col px-1 py-1 w-auto">
        <span className="text-xs text-purple-600 whitespace-nowrap">{t(`metrics.${metricId}`)}</span>
        <span className={`text-base font-bold ${textColorClass} whitespace-nowrap`}>
          {isNegative && isNegativeType ? '-' : ''}{formatCurrency(value)}
        </span>
      </div>
    );
  };

  // Si no hay métricas seleccionadas, no mostrar nada
  if (selectedMetrics.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border-t border-gray-200 px-1 py-2">
      <div className="flex items-center justify-between">
        {/* Métricas seleccionadas - orden fijo según METRICS */}
        <div className="flex-1 flex items-center justify-around overflow-x-auto">
          {METRIC_IDS.filter(id => selectedMetrics.includes(id)).map(metricId =>
            renderMetric(metricId)
          )}
        </div>
        
        {/* Engrane morado para abrir modal */}
        <button
          onClick={onOpenModal}
          className="ml-2 w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-purple-200 transition-colors flex-shrink-0"
          aria-label={t('metrics.configure')}
        >
          <i className="fas fa-cog text-purple-600 text-sm"></i>
        </button>
      </div>
    </div>
  );
};

// Exportar también las constantes para usar en el modal
export { METRICS, DEFAULT_SELECTED_METRICS, DEFAULT_TAX_RATE, DEFAULT_BUDGET, STORAGE_KEYS };
export default ListMetricsBar;